import React from "react";
import { useField, UseFieldConfig, useForm } from "react-final-form";

import { TextField as MuiTextField, TextFieldProps } from "@mui/material";
import { styled } from "@mui/material/styles";

export const Field = styled(MuiTextField)(({ theme }) => ({
    "& .MuiInputBase-root": {
        backgroundColor: theme.palette.emteria.background.input,
        borderRadius: 4,
    },
    "& .MuiInputBase-root.Mui-disabled": {
        backgroundColor: theme.palette.emteria.background.inputDisabled,
    },
    "& .Mui-focused .MuiInputBase-input": {
        borderColor: theme.palette.emteria.action,
    },
    "& .Mui-error .MuiInputBase-input": {
        borderColor: theme.palette.emteria.error,
    },
    "& .MuiInput-underline:after": {
        borderBottomColor: theme.palette.emteria.action,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.emteria.action,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.emteria.error,
    },
    "& .MuiFormHelperText-root": {
        color: theme.palette.emteria.action,
        marginTop: "0px",
    },
    "& .MuiFormHelperText-root.Mui-error": {
        color: theme.palette.emteria.error,
    },
    "& .MuiFormLabel-root.Mui-focused": {
        color: theme.palette.emteria.link,
    },
    "& .MuiFormLabel-root.Mui-error": {
        color: theme.palette.emteria.error,
    },
}));

type Props = TextFieldProps & {
    name: string;
    config?: UseFieldConfig;
    showErrorOnSubmit?: boolean;
};

export const TextField = ({
    name,
    config,
    variant = "outlined",
    size = "small",
    showErrorOnSubmit = false,
    onBlur,
    ...rest
}: Props) => {
    const { input, meta } = useField(name, config);
    const form = useForm();
    const error = form.getState().errors?.[name] || null;
    // cannot use meta.error from useField due to the limitations of react-final-form,
    // which does not recognize errors for fields with dot notation in meta.error

    const shouldShowError = showErrorOnSubmit ? error && meta.submitFailed && !meta.active : error && meta.touched;

    return (
        <Field
            {...input}
            {...rest}
            {...{ size, variant }}
            value={input.value ?? ""}
            onBlur={(event) => {
                input.onBlur(event);
                onBlur?.(event);
            }}
            error={shouldShowError}
            helperText={shouldShowError ? error : ""}
            style={{ ...rest.style }}
            slotProps={{
                ...rest.slotProps,
                htmlInput: { ...rest.slotProps?.htmlInput, "data-testid": "text-field" },
            }}
        />
    );
};

type UncontrolledTextFieldProps = TextFieldProps & {
    errorMessage?: string;
};

export const UncontrolledTextField = React.forwardRef<HTMLInputElement, UncontrolledTextFieldProps>(
    ({ variant = "outlined", size = "small", errorMessage, value, ...rest }, ref) => (
        <Field
            {...rest}
            {...{ size, variant }}
            value={value ?? ""}
            slotProps={{
                ...rest.slotProps,
                htmlInput: { ...rest.slotProps?.htmlInput, "data-testid": "text-field" },
            }}
            inputRef={ref}
            error={!!errorMessage}
            helperText={errorMessage}
        />
    ),
);
UncontrolledTextField.displayName = "UncontrolledTextField";
